import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer,
} from "recharts";
import {
  CheckCircle2, XCircle, Sparkles, Trophy, ArrowRight, RotateCcw, Bot, ChevronDown,
} from "lucide-react";
import Btn from "../components/Btn";
import BadgeIcon from "../components/BadgeIcon";
import MentorChat from "../components/MentorChat";
import RatingPrompt from "../components/RatingPrompt";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { QStimulus, OptMedia, hasMedia } from "../components/QMedia";

export default function Result() {
  const { attemptId } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [payload, setPayload] = useState(loc.state || null);
  const [attempt, setAttempt] = useState(null);
  const [showRating, setShowRating] = useState(false);

  useEffect(() => {
    const dismissed = Number(localStorage.getItem("su-rated-dismiss") || 0);
    const rated = localStorage.getItem("su-rated");
    if (!rated && Date.now() - dismissed > 3 * 86400000) {
      api.get("/ratings/me").then(({ data }) => {
        if (!data.rated && data.eligible) setTimeout(() => setShowRating(true), 2600);
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    refresh();
    if (!loc.state) {
      api.get(`/cbt/result/${attemptId}`).then(({ data }) => {
        setPayload({ result: data.result, gamification: null, ranking: data.ranking });
        setAttempt(data.attempt);
      }).catch(() => nav("/dashboard"));
    }
    // eslint-disable-next-line
  }, [attemptId]);

  if (!payload) return <div className="min-h-[60vh] grid place-items-center"><div className="w-12 h-12 rounded-full border-4 border-su-border border-t-su-primary animate-spin" /></div>;

  const { result, gamification } = payload;
  const ranking = payload.ranking;
  const leveledUp = gamification?.leveled_up;
  const newBadges = gamification?.new_badges || [];
  const radarData = result.radar || [];

  return (
    <div className="space-y-6 relative">
      {(leveledUp || newBadges.length > 0) && <Confetti />}
      {showRating && <RatingPrompt onClose={() => setShowRating(false)} />}

      {/* headline */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-chunky p-6 sm:p-8 text-center">
        <div className={`w-20 h-20 mx-auto rounded-3xl grid place-items-center mb-4 ${result.accuracy >= 60 ? "bg-su-success/15" : "bg-su-energy/15"}`}>
          <Trophy className={`w-10 h-10 ${result.accuracy >= 60 ? "text-su-success" : "text-su-energy"}`} />
        </div>
        <p className="font-heading font-extrabold text-su-muted uppercase text-sm tracking-wider">Hasil Tryout</p>
        <h1 className="font-heading font-black text-4xl sm:text-5xl my-2" data-testid="result-score">{result.total_score}</h1>
        <p className="text-su-muted font-medium">
          {result.total_correct}/{result.total_questions} benar · Akurasi <span className="font-heading font-black text-su-text">{result.accuracy}%</span>
          {result.passed === true && <span className="ml-2 text-su-success font-black">· LULUS ✅</span>}
          {result.passed === false && <span className="ml-2 text-su-danger font-black">· BELUM LULUS</span>}
        </p>
        {gamification && (
          <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
            <span className="inline-flex items-center gap-1.5 bg-su-primary/10 text-su-primary font-heading font-black px-4 py-2 rounded-full" data-testid="result-exp">
              <Sparkles className="w-4 h-4" /> +{gamification.exp_gained} EXP
            </span>
            {leveledUp && <span className="inline-flex items-center gap-1.5 bg-su-secondary/20 text-su-secondaryShadow font-heading font-black px-4 py-2 rounded-full animate-pop">🎉 Naik ke Level {gamification.new_level}!</span>}
          </div>
        )}
      </motion.div>

      {newBadges.length > 0 && (
        <div className="card-chunky p-6">
          <h2 className="font-heading font-black text-lg mb-4">Badge Baru Terbuka! 🏅</h2>
          <div className="flex gap-4 flex-wrap justify-center">
            {newBadges.map((b) => <div key={b.id} className="animate-pop"><BadgeIcon badge={b} /></div>)}
          </div>
        </div>
      )}

      {ranking && ranking.rank && <RankingCard ranking={ranking} />}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* radar */}
        <div className="card-chunky p-6">
          <h2 className="font-heading font-black text-lg mb-2">Analisis Kemampuan</h2>
          <p className="text-su-muted font-medium text-sm mb-4">Akurasi per subtopik (%)</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid stroke="var(--su-border)" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: "var(--su-muted)", fontSize: 11, fontWeight: 700 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "var(--su-muted)", fontSize: 10 }} />
                <Radar dataKey="accuracy" stroke="var(--su-primary)" fill="var(--su-primary)" fillOpacity={0.4} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* category scores */}
        <div className="card-chunky p-6">
          <h2 className="font-heading font-black text-lg mb-4">Skor per Kategori</h2>
          <div className="space-y-4">
            {Object.entries(result.category_scores).map(([cat, score]) => {
              const st = result.category_stats[cat] || {};
              const pct = st.total ? (st.correct / st.total) * 100 : 0;
              return (
                <div key={cat} data-testid={`catscore-${cat}`}>
                  <div className="flex items-center justify-between font-heading font-extrabold mb-1.5">
                    <span>{cat}</span>
                    <span className="font-mono">{score}</span>
                  </div>
                  <div className="h-3 rounded-full bg-su-track overflow-hidden">
                    <div className="h-full rounded-full bg-su-primary" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-xs text-su-muted font-bold mt-1">{st.correct || 0}/{st.total || 0} benar</p>
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 mt-6">
            <Link to="/tryouts" className="flex-1"><Btn variant="ghost" className="w-full"><RotateCcw className="w-4 h-4" /> Coba Lagi</Btn></Link>
            <Link to="/dashboard" className="flex-1"><Btn className="w-full">Dashboard <ArrowRight className="w-4 h-4" /></Btn></Link>
          </div>
        </div>
      </div>

      {/* review */}
      <div className="card-chunky p-6">
        <h2 className="font-heading font-black text-lg mb-1">Pembahasan</h2>
        <p className="text-su-muted font-medium text-sm mb-4">Pelajari pembahasan langkah demi langkah tiap soal.</p>
        <div className="space-y-3">
          {result.review.map((r, i) => <ReviewItem key={r.qid} r={r} num={i + 1} />)}
        </div>
      </div>
    </div>
  );
}

function RankingCard({ ranking }) {
  const { rank, total, top_percent, top } = ranking;
  const medal = (r) => (r === 1 ? "🥇" : r === 2 ? "🥈" : r === 3 ? "🥉" : `#${r}`);
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="card-chunky p-6" data-testid="result-ranking">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-5 h-5 text-su-secondary" />
        <h2 className="font-heading font-black text-lg">Peringkat Tryout</h2>
      </div>
      <div className="flex flex-wrap items-center gap-4 mb-5">
        <div className="flex-1 min-w-[160px] rounded-2xl border-2 border-su-secondary/40 bg-su-secondary/10 p-4 text-center">
          <p className="text-xs font-heading font-black uppercase text-su-muted">Peringkat Kamu</p>
          <p className="font-heading font-black text-3xl text-su-secondaryShadow" data-testid="ranking-position">#{rank} <span className="text-lg text-su-muted">/ {total}</span></p>
        </div>
        {top_percent != null && (
          <div className="flex-1 min-w-[160px] rounded-2xl border-2 border-su-success/40 bg-su-success/10 p-4 text-center">
            <p className="text-xs font-heading font-black uppercase text-su-muted">Posisi</p>
            <p className="font-heading font-black text-3xl text-su-success">Top {top_percent}%</p>
          </div>
        )}
      </div>
      <p className="text-xs font-heading font-black uppercase text-su-muted mb-2">Papan Peringkat (Top {top.length})</p>
      <div className="space-y-2">
        {top.map((t) => (
          <div key={t.rank} data-testid={`ranking-row-${t.rank}`}
            className={`flex items-center gap-3 rounded-xl px-3 py-2 border-2 ${t.is_me ? "border-su-primary bg-su-primary/10" : "border-su-border bg-su-surface"}`}>
            <span className="font-heading font-black w-8 text-center">{medal(t.rank)}</span>
            <span className="flex-1 font-heading font-extrabold text-sm truncate">
              {t.name}{t.is_me && <span className="ml-2 text-xs text-su-primary font-black">(Kamu)</span>}
            </span>
            <span className="text-xs text-su-muted font-bold">{t.accuracy}%</span>
            <span className="font-heading font-black">{t.score}</span>
          </div>
        ))}
        {!top.some((t) => t.is_me) && rank > top.length && (
          <div data-testid="ranking-row-me" className="flex items-center gap-3 rounded-xl px-3 py-2 border-2 border-su-primary bg-su-primary/10">
            <span className="font-heading font-black w-8 text-center">#{rank}</span>
            <span className="flex-1 font-heading font-extrabold text-sm">Kamu <span className="ml-1 text-xs text-su-primary font-black">(Kamu)</span></span>
            <span className="font-heading font-black">{ranking.my_score}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function ReviewItem({ r, num }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-2xl border-2 overflow-hidden ${r.is_correct ? "border-su-success/40" : "border-su-danger/40"}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`review-${num}`} className="w-full flex items-center gap-3 p-4 text-left">
        {r.is_correct ? <CheckCircle2 className="w-5 h-5 text-su-success shrink-0" /> : <XCircle className="w-5 h-5 text-su-danger shrink-0" />}
        <span className="flex-1 font-heading font-extrabold text-sm line-clamp-1">{num}. {r.question_text}</span>
        <ChevronDown className={`w-5 h-5 text-su-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p className="font-medium text-sm whitespace-pre-line">{r.question_text}</p>
          <QStimulus q={r} />
          <div className="space-y-1.5">
            {r.options.map((o) => {
              const isCorrect = o.key === r.correct_answer;
              const isUser = o.key === r.user_answer;
              return (
                <div key={o.key} className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium ${
                  isCorrect ? "bg-su-success/15 text-su-success" : isUser ? "bg-su-danger/15 text-su-danger" : "text-su-muted"
                }`}>
                  <span className="font-heading font-black">{o.key}.</span> {hasMedia(o) ? <OptMedia opt={o} /> : o.text}
                  {isCorrect && <CheckCircle2 className="w-4 h-4 ml-auto" />}
                  {isUser && !isCorrect && <XCircle className="w-4 h-4 ml-auto" />}
                </div>
              );
            })}
          </div>
          <div className="rounded-2xl bg-su-primary/5 border-2 border-su-primary/20 p-4 space-y-3">
            <div>
              <div className="flex items-center gap-2 mb-2 text-su-primary font-heading font-black text-sm">
                <Bot className="w-4 h-4" /> Pembahasan
              </div>
              <p className="font-medium text-sm text-su-text/90">{r.explanation}</p>
            </div>
            <MentorChat qid={r.qid} userAnswer={r.user_answer} />
          </div>
        </div>
      )}
    </div>
  );
}

function Confetti() {
  const colors = ["#1CB0F6", "#58CC02", "#FFC800", "#FF4B4B", "#FF9600"];
  const pieces = Array.from({ length: 60 });
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
      {pieces.map((_, i) => (
        <motion.div
          key={i}
          initial={{ y: -40, x: `${Math.random() * 100}vw`, rotate: 0, opacity: 1 }}
          animate={{ y: "110vh", rotate: 360 * (Math.random() > 0.5 ? 1 : -1) }}
          transition={{ duration: 2.2 + Math.random() * 1.5, delay: Math.random() * 0.5, ease: "easeIn" }}
          style={{ position: "absolute", width: 10, height: 14, background: colors[i % colors.length], borderRadius: 3 }}
        />
      ))}
    </div>
  );
}
